import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useHistory, useParams } from "react-router-dom";
import UserIdentitiesService from "@/services/UserIdentitiesService";
import savingsPlans from "@/services/SavingsPlanService";
import savingsPlansUserIdentitiesService, { IdentitiesSelectionAssignment, IdentitiesSelectionIdentity } from "@/services/SavingsPlanUserIdentityService";
import { ContractOnboardingStrings, resolveContractOnboardingError } from "@/screen/Map/contractOnboarding.strings";
import { IntervenerRelation, MinorToAdultSelectionStepKey, isIdentitySelectableForContract, isRelationLockedForMinorToAdult } from "@/screen/ContractManagement/helpers/intervenerSelectionHelpers";
import { buildMapNavigationState } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import { IdentitiesPath } from "@/screen/Identities/identitiesRoutes";
import type { IntervenerSelectionControllerReturn, KycOwnerNotifyStatus } from "@/screen/ContractManagement/Interfaces/IntervenerSelectionInterfaces";

/** Asignación en edición: id ausente = alta local aún no persistida. */
type WorkingAssignment = Omit<IdentitiesSelectionAssignment, "id"> & {id?: number};


export function useIntervenerSelectionController(isReadyToLoad: boolean, activeStepKey: string | null): IntervenerSelectionControllerReturn
{
    const { savingsPlanId: savingsPlanIdParam } = useParams<{ savingsPlanId: string }>();
    const savingsPlanId = Number(savingsPlanIdParam);
    const history = useHistory();

    const [userIdentities, setUserIdentities] = useState<IdentitiesSelectionIdentity[]>([]);
    const [savingsPlanName, setSavingsPlanName] = useState<string | undefined>(undefined);
    const [assignments, setAssignments] = useState<WorkingAssignment[]>([]);
    const [initialStoredAssignments, setInitialStoredAssignments] = useState<IdentitiesSelectionAssignment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [notifiedOwners, setNotifiedOwners] = useState<Record<number, KycOwnerNotifyStatus>>({});
    const [notifyingIdentityId, setNotifyingIdentityId] = useState<number | undefined>(undefined);

    useEffect(() =>
    {
        let cancelled = false;

        const load = async () =>
        {
            // Esperamos a que la guarda de paso confirme 2.2 antes de pedir el catálogo.
            if(!isReadyToLoad)
            {
                return;
            }

            if(Number.isNaN(savingsPlanId))
            {
                setIsLoading(false);
                setErrorMessage("No se ha podido cargar la selección de intervinientes.");
                return;
            }

            setIsLoading(true);
            setErrorMessage(null);

            const envelope = await savingsPlansUserIdentitiesService.getIdentitiesSelection(savingsPlanId);

            if(cancelled)
            {
                return;
            }

            if(!envelope.success || !envelope.data)
            {
                setErrorMessage(resolveContractOnboardingError(envelope.errors?.[0], "No se ha podido cargar la selección de intervinientes."));
                setIsLoading(false);
                return;
            }

            setSavingsPlanName(envelope.data.savingsPlanName || undefined);
            setUserIdentities(envelope.data.userIdentities);
            setAssignments(envelope.data.assignments);
            setInitialStoredAssignments(envelope.data.assignments);
            setIsLoading(false);
        };

        void load();
        return () =>
        {
            cancelled = true;
        };
    }, [savingsPlanId, isReadyToLoad]);

    const hasAnyMinor = useMemo(() =>
    {
        return userIdentities.some((identity) => identity.isMinor);
    }, [userIdentities]);

    const isChecked = useCallback((userIdentityId: number | undefined, value: number | undefined) =>
    {
        const row = assignments.find((assignment) => assignment.userIdentityId === userIdentityId);
        if(row !== undefined)
        {
            return row.relation === value;
        }
        return value === undefined;
    }, [assignments]);

    // Solo el espejo de mayoría de edad restringe roles; en 2.2 la selección es libre.
    const isRelationLocked = useCallback((userIdentityId: number | undefined, relation: number | undefined) =>
    {
        if(activeStepKey !== MinorToAdultSelectionStepKey)
        {
            return false;
        }

        const row = assignments.find((assignment) => assignment.userIdentityId === userIdentityId);
        return isRelationLockedForMinorToAdult(row?.relation, relation);
    }, [activeStepKey, assignments]);

    const onRelationChange = useCallback((event: ChangeEvent<HTMLInputElement>, userIdentityId: number | undefined) =>
    {
        const rawValue = event.target.value;
        const existing = assignments.find((assignment) => assignment.userIdentityId === userIdentityId);
        const selectedOwner = assignments.find((assignment) => assignment.relation === IntervenerRelation.Owner);

        if(selectedOwner !== undefined && rawValue === String(IntervenerRelation.Owner))
        {
            return;
        }

        if(rawValue !== undefined && rawValue !== "")
        {
            const relation = parseInt(rawValue, 10);
            if(existing !== undefined)
            {
                const updated: WorkingAssignment =
                {
                    ...existing,
                    relation,
                };
                setAssignments((prev) =>
                [
                    ...prev.filter((assignment) => assignment.userIdentityId !== userIdentityId),
                    updated,
                ]);
                return;
            }

            if(userIdentityId !== undefined)
            {
                const created: WorkingAssignment =
                {
                    userIdentityId,
                    savingsPlanId,
                    relation,
                };
                setAssignments((prev) =>
                [
                    ...prev.filter((assignment) => assignment.userIdentityId !== userIdentityId),
                    created,
                ]);
            }
            return;
        }

        setAssignments((prev) => prev.filter((assignment) => assignment.userIdentityId !== userIdentityId));
    }, [assignments, savingsPlanId]);

    const onNotifyOwner = useCallback(async (userIdentityId: number) =>
    {
        setNotifyingIdentityId(userIdentityId);
        const response = await UserIdentitiesService.notifyKycOwner(userIdentityId);
        setNotifyingIdentityId(undefined);

        if(response.success)
        {
            setNotifiedOwners((previous) =>
            ({
                ...previous,
                [userIdentityId]: response.data?.alreadyNotified === true ? "already" : "sent",
            }));
            return;
        }

        setErrorMessage(response.errors?.[0]?.message ?? ContractOnboardingStrings.intervenerSelectionNotifyFailed);
    }, []);

    const onBackToMap = useCallback(() =>
    {
        history.push("/configurationMap", buildMapNavigationState(savingsPlanId, savingsPlanName));
    }, [history, savingsPlanName, savingsPlanId]);

    const onGoToIdentities = useCallback(() =>
    {
        history.push(IdentitiesPath, {contractSavingsPlanId: savingsPlanId});
    }, [history, savingsPlanId]);

    const applySelectionData = useCallback((data: {userIdentities: IdentitiesSelectionIdentity[]; assignments: IdentitiesSelectionAssignment[]; savingsPlanName: string}) =>
    {
        setSavingsPlanName(data.savingsPlanName || undefined);
        setUserIdentities(data.userIdentities);
        setAssignments(data.assignments);
        setInitialStoredAssignments(data.assignments);
    }, []);

    const onSubmit = useCallback(async () =>
    {
        if(Number.isNaN(savingsPlanId) || assignments.length === 0)
        {
            return;
        }

        setIsSubmitting(true);
        setErrorMessage(null);

        try
        {
            for(const assignment of assignments)
            {
                if(assignment.relation === 0)
                {
                    continue;
                }

                if(assignment.id !== undefined)
                {
                    await savingsPlansUserIdentitiesService.updateSavingsPlanUserIdentity(
                    {
                        id: assignment.id,
                        userIdentityId: assignment.userIdentityId,
                        savingsPlanId: assignment.savingsPlanId,
                        relation: assignment.relation,
                    });
                }
                else
                {
                    await savingsPlansUserIdentitiesService.createSavingsPlanUserIdentity(
                    {
                        userIdentityId: assignment.userIdentityId,
                        savingsPlanId,
                        relation: assignment.relation,
                    });
                }
            }

            for(const stored of initialStoredAssignments)
            {
                if(assignments.find((assignment) => assignment.id === stored.id) === undefined)
                {
                    await savingsPlansUserIdentitiesService.deleteSavingsPlanUserIdentity(stored.id);
                }
            }

            // Recargamos el estado desde el servidor antes de avanzar. Así, si el avance
            // falla y el usuario reintenta, los elementos creados ya tienen su id y entran
            // por la rama de update en lugar de duplicarse, y los borrados no se repiten.
            const refreshed = await savingsPlansUserIdentitiesService.getIdentitiesSelection(savingsPlanId);
            if(refreshed.success && refreshed.data)
            {
                applySelectionData(refreshed.data);
            }

            // Guarda de sincronía: se declara el paso que la guarda resolvió como vivo (2.2 en el tronco,
            // 2.4.2 en la rama de mayoría de edad). Si el plan estuviera en otro, el backend rechaza con
            // STEP_MISMATCH en lugar de avanzar un paso distinto.
            if(activeStepKey === null)
            {
                setIsSubmitting(false);
                return;
            }

            const advanceResult = await savingsPlans.advancePlanStep(savingsPlanId, activeStepKey);
            if(!advanceResult.success)
            {
                setErrorMessage(resolveContractOnboardingError(advanceResult.errors?.[0], ContractOnboardingStrings.continueContractFallbackError));
                setIsSubmitting(false);
                return;
            }

            history.push("/configurationMap", buildMapNavigationState(savingsPlanId, savingsPlanName));
        }
        catch(error: any)
        {
            // Si falla a mitad del create/update/delete (o la propia recarga previa al avance),
            // los ya persistidos pueden seguir sin id en memoria. Recargamos best-effort para
            // que el reintento entre por update y no duplique filas.
            try
            {
                const refreshedAfterError = await savingsPlansUserIdentitiesService.getIdentitiesSelection(savingsPlanId);
                if(refreshedAfterError.success && refreshedAfterError.data)
                {
                    applySelectionData(refreshedAfterError.data);
                }
            }
            catch
            {
                // Si la recarga también falla, al menos mostramos el error original del submit.
            }

            const wireError = error?.response?.data?.Errors?.[0] ?? error?.response?.data?.errors?.[0];
            const envelopeError = wireError ? {code: wireError.Code ?? wireError.code, message: wireError.Message ?? wireError.message} : null;
            setErrorMessage(resolveContractOnboardingError(envelopeError, error?.message ?? ContractOnboardingStrings.continueContractFallbackError));
            setIsSubmitting(false);
        }
    }, [activeStepKey, applySelectionData, assignments, history, initialStoredAssignments, savingsPlanName, savingsPlanId]);

    // Salvaguarda: si por fallo de datos no hay ninguna identidad seleccionable, no dejamos
    // continuar y ofrecemos ir a gestionar intervinientes. En contrato normal el backend
    // siempre incluye al menos la main seleccionable; esto cubre respuestas anómalas.
    useEffect(() =>
    {
        if(isLoading || !isReadyToLoad)
        {
            return;
        }

        const anySelectable = userIdentities.some(isIdentitySelectableForContract);
        if(!anySelectable)
        {
            setErrorMessage((prev) => prev ?? ContractOnboardingStrings.intervenerSelectionNotReady);
        }
    }, [isLoading, isReadyToLoad, userIdentities]);

    // Evita que se escriban asignaciones inválidas que el backend rechazaría.
    // El backend sigue siendo la fuente de verdad; esto solo bloquea combinaciones inviables.
    const validationError = useMemo<string | null>(() =>
    {
        const selected = assignments.filter((assignment) => assignment.relation > 0);
        if(selected.length === 0)
        {
            return null;
        }

        // El backend rechaza el rol de un interviniente no seleccionable; sin esto el error llegaba tras pulsar Continuar.
        const notSelectableIncluded = selected
            .map((assignment) => userIdentities.find((identity) => identity.id === assignment.userIdentityId))
            .find((identity) => identity?.isSelectable === false);
        if(notSelectableIncluded !== undefined)
        {
            const notSelectableFullName = [notSelectableIncluded.firstName, notSelectableIncluded.lastName, notSelectableIncluded.secondLastName].filter(Boolean).join(" ").trim();
            return ContractOnboardingStrings.intervenerSelectionNotSelectableIncluded(notSelectableFullName);
        }

        const anyMinorSelected = selected.some((assignment) =>
        {
            const identity = userIdentities.find((item) => item.id === assignment.userIdentityId);
            return identity?.isMinor === true;
        });
        const anyRepresentativeSelected = selected.some((assignment) => assignment.relation === IntervenerRelation.LegalRepresentative);

        if(anyMinorSelected && !anyRepresentativeSelected)
        {
            return ContractOnboardingStrings.intervenerSelectionMinorNeedsRepresentative;
        }
        if(!anyMinorSelected && anyRepresentativeSelected)
        {
            return ContractOnboardingStrings.intervenerSelectionRepresentativeNeedsMinor;
        }

        const selectedOwner = selected.find((assignment) => assignment.relation === IntervenerRelation.Owner);
        if(selectedOwner === undefined)
        {
            return ContractOnboardingStrings.intervenerSelectionOwnerRequired;
        }

        // La identidad principal la marca el backend (IsMainIdentity); debe ir siempre en el contrato.
        const mainIdentity = userIdentities.find((identity) => identity.isMainIdentity);
        if(mainIdentity === undefined)
        {
            return ContractOnboardingStrings.intervenerSelectionMainIdentityUnknown;
        }

        const mainIdentitySelected = selected.find((assignment) => assignment.userIdentityId === mainIdentity.id);
        if(mainIdentitySelected === undefined)
        {
            const mainIdentityFullName = [mainIdentity.firstName, mainIdentity.lastName, mainIdentity.secondLastName].filter(Boolean).join(" ").trim();
            return ContractOnboardingStrings.intervenerSelectionMainIdentityRequired(mainIdentityFullName);
        }

        return null;
    }, [assignments, userIdentities]);

    const canSubmit = !isLoading
        && !isSubmitting
        && validationError === null
        && assignments.some((assignment) => assignment.relation > 0)
        && userIdentities.some(isIdentitySelectableForContract);

    return {
        savingsPlanId,
        savingsPlanName,
        userIdentities,
        isLoading,
        isSubmitting,
        errorMessage,
        hasAnyMinor,
        notifiedOwners,
        notifyingIdentityId,
        isChecked,
        isRelationLocked,
        onRelationChange,
        onNotifyOwner,
        onSubmit,
        onBackToMap,
        onGoToIdentities,
        canSubmit,
        validationError,
    };
}